<template>
  <v-card tile id="calendar-view">
    <v-card-title>
      <v-toolbar flat>
        <v-btn icon @click="prev">
          <v-icon>chevron_left</v-icon>
        </v-btn>
        <v-btn icon @click="next">
          <v-icon>chevron_right</v-icon>
        </v-btn>
        <v-toolbar-title class="calendar-title">{{ calendarTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-select
          v-if="isSysAdmin"
          v-model="filterCompany"
          :items="allCompaniesOptions"
          :label="resx.labels.company"
          item-text="name"
          item-value="_id"
          class="company-filter mr-4"
          menu-props="offsetY"
          clearable
          hide-details
          :no-data-text="resx.messages.noSelectorDataAvailable"
          :disabled="companies.length === 0"
        />
        <v-btn-toggle v-model="calendarType" mandatory class="mr-2">
          <v-btn small value="month">{{ resx.labels.month }}</v-btn>
          <v-btn small value="week">{{ resx.labels.week }}</v-btn>
          <v-btn small value="day">{{ resx.labels.day }}</v-btn>
        </v-btn-toggle>
        <v-btn small outlined @click="setToday" class="ml-2">
          {{ resx.labels.today }}
        </v-btn>
      </v-toolbar>
    </v-card-title>

    <v-card-text class="pa-2">
      <v-calendar
        ref="calendar"
        v-model="focus"
        :type="calendarType"
        :events="calendarEvents"
        :event-color="e => e.color"
        event-overlap-mode="stack"
        locale="es"
        @click:event="onEventClick"
        @click:date="onDateClick"
        @click:time="onTimeClick"
        @change="onCalendarChange"
      >
        <template #event="{ event, timed }">
          <div class="v-event-summary text-truncate pl-1">
            <template v-if="timed">
              <strong>{{ formatStartTime(event) }}</strong>
              {{ event.title }}
            </template>
            <template v-else>{{ event.title }}</template>
          </div>
        </template>
        <template #day-label="{ day, date, present }">
          <v-btn
            depressed
            fab
            small
            :color="present ? 'primary' : 'transparent'"
            :dark="present"
            @click.stop="onDateClick({ date })"
          >{{ day }}</v-btn>
        </template>
        <template #day="{ date }">
          <div v-if="calendarType === 'month'" class="day-click-area" @click="onDateClick({ date })"></div>
        </template>
      </v-calendar>
    </v-card-text>

    <v-dialog v-model="showDialog" max-width="560" @click:outside="closeDialog">
      <v-card>
        <v-app-bar dark flat color="primary">
          <v-toolbar-items @click="closeDialog">
            <v-icon>arrow_back</v-icon>
          </v-toolbar-items>
          <v-toolbar-title>
            {{ editingEvent._id ? resx.labels.editEvent : resx.labels.newEvent }}
          </v-toolbar-title>
        </v-app-bar>

        <v-card-text class="pt-4">
          <v-text-field
            v-model="editingEvent.title"
            :label="resx.labels.eventTitle"
            required
          />
          <v-text-field
            v-model="editingEvent.description"
            :label="resx.labels.description"
          />

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="editingEvent.startDatePart"
                :label="resx.labels.startDate"
                type="date"
                required
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="editingEvent.startTimePart"
                :label="resx.labels.startTime"
                type="time"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="editingEvent.endDatePart"
                :label="resx.labels.endDate"
                type="date"
                required
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="editingEvent.endTimePart"
                :label="resx.labels.endTime"
                type="time"
                required
              />
            </v-col>
          </v-row>

          <v-select
            v-if="isSysAdmin"
            v-model="editingEvent.company"
            :items="companies"
            :label="resx.labels.company"
            item-text="name"
            item-value="_id"
            required
            :no-data-text="resx.messages.noSelectorDataAvailable"
            :disabled="companies.length === 0"
          />

          <v-select
            v-model="editingEvent.contractor"
            :items="filteredContractors"
            :label="resx.labels.availableContractors"
            :item-text="c => c.contractor && c.contractor.name ? c.contractor.name : ''"
            item-value="_id"
            clearable
            :no-data-text="resx.messages.noSelectorDataAvailable"
            :disabled="contractors.length === 0"
          />

          <v-select
            v-model="editingEvent.color"
            :items="colorOptions"
            :label="resx.labels.color || 'Color'"
            item-text="label"
            item-value="value"
          >
            <template v-slot:item="{ item }">
              <v-icon :color="item.value" class="mr-2">circle</v-icon>
              {{ item.label }}
            </template>
            <template v-slot:selection="{ item }">
              <v-icon :color="item.value" class="mr-2">circle</v-icon>
              {{ item.label }}
            </template>
          </v-select>

          <v-alert v-if="dialogError" type="error" dense class="mt-2">
            {{ dialogError }}
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="editingEvent._id && canManage"
            color="error"
            text
            @click="deleteEvent"
          >
            {{ resx.labels.deleteEvent }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">{{ resx.labels.cancel }}</v-btn>
          <v-btn
            v-if="canManage"
            color="primary"
            @click="saveEvent"
          >
            {{ resx.labels.save }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from "moment";
import companyService from "@/services/CompanyService";
import calendarEventService from "@/services/CalendarEventService";
import contractorCompanyService from "@/services/ContractorCompanyService";

const COMPANY_COLORS = [
  "blue", "red", "green", "orange", "purple",
  "teal", "pink", "indigo", "cyan", "deep-orange"
];

const COLOR_OPTIONS = [
  { label: "Azul",       value: "blue" },
  { label: "Rojo",       value: "red" },
  { label: "Verde",      value: "green" },
  { label: "Naranja",    value: "orange" },
  { label: "Violeta",    value: "purple" },
  { label: "Teal",       value: "teal" },
  { label: "Rosa",       value: "pink" },
  { label: "Índigo",     value: "indigo" },
  { label: "Cyan",       value: "cyan" },
  { label: "Primario",   value: "primary" }
];

const emptyEvent = () => ({
  _id: null,
  title: "",
  description: "",
  startDatePart: "",
  startTimePart: "08:00",
  endDatePart: "",
  endTimePart: "09:00",
  color: "primary",
  company: null,
  contractor: null
});

export default {
  name: "calendarView",

  data() {
    const resx = this.$store.state.locale.resx;
    return {
      resx,
      focus: "",
      calendarType: "month",
      calendarTitle: "",
      events: [],
      companies: [],
      contractors: [],
      filterCompany: null,
      showDialog: false,
      editingEvent: emptyEvent(),
      dialogError: null,
      colorOptions: COLOR_OPTIONS
    };
  },

  computed: {
    isSysAdmin() {
      return this.$store.state.auth.user.roles.includes(
        this.$store.state.constants.roles.SYS_ADMIN
      );
    },
    canManage() {
      const roles = this.$store.state.auth.user.roles;
      const { SYS_ADMIN, ADMIN } = this.$store.state.constants.roles;
      return roles.includes(SYS_ADMIN) || roles.includes(ADMIN);
    },
    allCompaniesOptions() {
      return this.companies;
    },
    filteredContractors() {
      const companyId = this.editingEvent.company;
      if (!companyId) return this.contractors;
      return this.contractors.filter(c => {
        const id = c.company && c.company._id ? c.company._id : c.company;
        return id == companyId;
      });
    },
    calendarEvents() {
      const filtered = this.filterCompany
        ? this.events.filter(e => {
            const id = e.company && e.company._id ? e.company._id : e.company;
            return id == this.filterCompany;
          })
        : this.events;

      return filtered.map(e => ({
        ...e,
        name: e.title,
        start: new Date(e.startDate),
        end: new Date(e.endDate),
        timed: true,
        color: e.color || this.colorForCompany(e.company)
      }));
    }
  },

  async mounted() {
    if (this.isSysAdmin) {
      this.companies = await companyService.getCompanies(true);
    }
    this.contractors = await contractorCompanyService.list();
    await this.loadEvents();
  },

  watch: {
    filterCompany() {
      this.loadEvents();
    }
  },

  methods: {
    async loadEvents() {
      const companyId = this.isSysAdmin ? this.filterCompany : null;
      this.events = await calendarEventService.getEvents(companyId);
    },

    colorForCompany(company) {
      if (!company) return "primary";
      const id = company._id || company;
      const idx = this.companies.findIndex(c => c._id == id);
      return COMPANY_COLORS[idx >= 0 ? idx % COMPANY_COLORS.length : 0];
    },

    formatStartTime(event) {
      const h = String(event.start.getHours()).padStart(2, "0");
      const m = String(event.start.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    },

    setToday() {
      this.focus = "";
    },

    prev() {
      this.$refs.calendar.prev();
    },

    next() {
      this.$refs.calendar.next();
    },

    onCalendarChange({ start }) {
      const d = moment(start.date);
      const capitalize = str => str.replace(/^\w/, c => c.toUpperCase());
      if (this.calendarType === "month") {
        this.calendarTitle = capitalize(d.format("MMMM YYYY"));
      } else if (this.calendarType === "week") {
        this.calendarTitle = `Semana del ${d.format("DD/MM/YYYY")}`;
      } else {
        this.calendarTitle = capitalize(d.format("dddd DD/MM/YYYY"));
      }
    },

    onDateClick({ date }) {
      this.openNewEvent(date, "00:00", date, "23:59");
    },

    onTimeClick({ date, time }) {
      const endTime = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm")
        .add(1, "hour")
        .format("HH:mm");
      this.openNewEvent(date, time, date, endTime);
    },

    onEventClick({ event, nativeEvent }) {
      nativeEvent.stopPropagation();
      this.editingEvent = {
        ...emptyEvent(),
        _id: event._id,
        title: event.title,
        description: event.description || "",
        startDatePart: moment(event.startDate).format("YYYY-MM-DD"),
        startTimePart: moment(event.startDate).format("HH:mm"),
        endDatePart: moment(event.endDate).format("YYYY-MM-DD"),
        endTimePart: moment(event.endDate).format("HH:mm"),
        color: event.color || "primary",
        company: event.company ? (event.company._id || event.company) : null,
        contractor: event.contractor || null
      };
      this.dialogError = null;
      this.showDialog = true;
    },

    openNewEvent(startDate, startTime, endDate, endTime) {
      if (!this.canManage) return;
      this.editingEvent = {
        ...emptyEvent(),
        startDatePart: startDate,
        startTimePart: startTime,
        endDatePart: endDate,
        endTimePart: endTime,
        company: this.isSysAdmin ? this.filterCompany : null
      };
      this.dialogError = null;
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingEvent = emptyEvent();
      this.dialogError = null;
    },

    buildPayload() {
      const startDate = moment(
        `${this.editingEvent.startDatePart} ${this.editingEvent.startTimePart}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString();
      const endDate = moment(
        `${this.editingEvent.endDatePart} ${this.editingEvent.endTimePart}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString();
      return {
        _id: this.editingEvent._id,
        title: this.editingEvent.title,
        description: this.editingEvent.description,
        startDate,
        endDate,
        color: this.editingEvent.color,
        company: this.isSysAdmin
          ? this.editingEvent.company
          : this.$store.state.auth.user.company._id,
        contractor: this.editingEvent.contractor || null
      };
    },

    validate(payload) {
      if (!payload.title) {
        return this.resx.labels.eventTitle + " es requerido";
      }
      if (!payload.startDate || !payload.endDate) {
        return "Las fechas son requeridas";
      }
      const start = moment(payload.startDate);
      const end = moment(payload.endDate);
      if (end.isSameOrBefore(start)) {
        return "La hora de fin debe ser posterior a la de inicio";
      }
      if (end.diff(start, "hours") > 24) {
        return "El evento no puede durar más de un día";
      }
      if (this.isSysAdmin && !payload.company) {
        return this.resx.labels.company + " es requerido";
      }
      return null;
    },

    async saveEvent() {
      const payload = this.buildPayload();
      const error = this.validate(payload);
      if (error) {
        this.dialogError = error;
        return;
      }
      if (payload._id) {
        await calendarEventService.updateEvent(payload);
      } else {
        await calendarEventService.createEvent(payload);
      }
      await this.loadEvents();
      this.closeDialog();
    },

    async deleteEvent() {
      await calendarEventService.deleteEvent(this.editingEvent._id);
      await this.loadEvents();
      this.closeDialog();
    }
  }
};
</script>

<style lang="scss">
#calendar-view {
  height: 100%;

  .v-card__text {
    height: calc(100% - 64px);
  }

  .v-calendar {
    height: 100%;

    // Mayúsculas en encabezados de días y meses internos
    .v-calendar-weekly__head-weekday,
    .v-calendar-daily__head-day-label,
    .v-calendar-daily__head-weekday {
      text-transform: capitalize;
    }

    // Cursor pointer en celdas clickeables
    .v-calendar-weekly__day,
    .v-calendar-daily__day-interval {
      cursor: pointer;
    }

    // Cursor pointer en eventos (z-index por encima del área clickeable del día)
    .v-event,
    .v-event-timed {
      cursor: pointer;
      z-index: 1;
    }

    // Cursor pointer en números de día (z-index por encima del área clickeable)
    .v-calendar-weekly__day-label .v-btn,
    .v-calendar-daily__head-day-label .v-btn {
      cursor: pointer;
      position: relative;
      z-index: 1;
    }

    // Área transparente para detectar clicks en todo el rectángulo del día
    .day-click-area {
      position: absolute;
      inset: 0;
      z-index: 0;
      cursor: pointer;
    }
  }

  .company-filter {
    max-width: 220px;

    .v-text-field__details {
      display: none;
    }
  }

  .calendar-title {
    min-width: 200px;
  }
}

@media (max-width: 599px) {
  #calendar-view {
    height: auto;
    min-height: 100%;

    .v-card__text {
      height: auto;
      min-height: 450px;
    }

    .v-card__title {
      padding: 0 !important;
    }

    .v-toolbar--flat {
      height: auto !important;
    }

    .v-toolbar--flat .v-toolbar__content {
      flex-wrap: wrap !important;
      height: auto !important;
      padding: 6px 8px !important;
      gap: 4px;
    }

    .calendar-title {
      min-width: unset !important;
      font-size: 0.85rem;
      flex: 1;
    }

    .v-spacer {
      display: none;
    }

    .company-filter {
      order: 10;
      flex: 1 1 100%;
      max-width: 100% !important;
      margin-right: 0 !important;
    }

    .v-btn-toggle .v-btn {
      min-width: 44px !important;
      font-size: 0.72rem !important;
    }

    .v-calendar-weekly__head-weekday {
      font-size: 0.6rem !important;
      padding: 2px 0 !important;
    }

    .v-calendar-weekly__day-label .v-btn {
      width: 20px !important;
      height: 20px !important;
      font-size: 0.62rem !important;
    }

    .v-event,
    .v-event-timed {
      font-size: 0.65rem;
    }

    .v-event-summary {
      font-size: 0.65rem;
    }
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  #calendar-view {
    .calendar-title {
      min-width: 140px;
      font-size: 0.95rem;
    }

    .company-filter {
      max-width: 160px;
    }

    .v-calendar-weekly__head-weekday {
      font-size: 0.75rem;
    }
  }
}
</style>
